import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { createBrowserHistory } from 'history';

export interface TelemetryConfig {
  connectionString?: string;
  instrumentationKey?: string;
  environment?: string;
  additionalContext?: Record<string, any>;
  correlationHeaderExcludedDomains?: string[];
}

class TelemetryService {
  private appInsights: ApplicationInsights | null = null;
  private reactPlugin: ReactPlugin | null = null;
  private initialized = false;
  private config: TelemetryConfig = {};

  initialize(config: TelemetryConfig): void {
    try {
      if (this.initialized) {
        return;
      }

      this.config = config;

      const browserHistory = createBrowserHistory();
      this.reactPlugin = new ReactPlugin();

      this.appInsights = new ApplicationInsights({
        config: {
          connectionString: config.connectionString,
          instrumentationKey: config.instrumentationKey,
          enableAutoRouteTracking: true,
          enableCorsCorrelation: true,
          enableRequestHeaderTracking: true,
          enableResponseHeaderTracking: true,
          // With enableCorsCorrelation:true, App Insights injects
          // traceparent/Request-Id/Request-Context headers into every
          // cross-origin fetch/XHR. Those headers must NOT be added to the
          // Datadog RUM/Logs intake (or Piano), otherwise the browser turns
          // the SDK's CORS-"simple" intake POST into a preflighted request
          // the intake rejects -> "CORS error" on every fetch-transport batch.
          // The excluded-domain list is sourced from telemetryConfig.ts.
          correlationHeaderExcludedDomains: config.correlationHeaderExcludedDomains,
          extensions: [this.reactPlugin],
          extensionConfig: {
            [this.reactPlugin.identifier]: { history: browserHistory },
          },
        },
      });

      this.appInsights.loadAppInsights();

      if (this.appInsights && this.appInsights.context) {
        if (config.environment) {
          (this.appInsights.context as any)['ai.cloud.role'] = `webapp-${config.environment}`;
        }

        if (config.additionalContext) {
          Object.entries(config.additionalContext).forEach(([key, value]) => {
            (this.appInsights!.context as any)[`custom.${key}`] = String(value);
          });
        }
      }

      this.initialized = true;
    } catch (error) {
      console.error('Failed to initialize telemetry:', error);
    }
  }

  trackException(error: Error, properties?: Record<string, any>): void {
    try {
      if (!this.initialized || !this.appInsights) {
        return;
      }

      this.appInsights.trackException({
        exception: error,
        properties,
      });
    } catch (err) {
      console.error('Failed to track exception:', err);
    }
  }

  trackPageView(name?: string, properties?: Record<string, any>): void {
    try {
      if (!this.initialized || !this.appInsights) {
        return;
      }

      this.appInsights.trackPageView({
        name,
        properties,
      });
    } catch (error) {
      console.error('Failed to track page view:', error);
    }
  }

  trackEvent(name: string, properties?: Record<string, any>): void {
    try {
      if (!this.initialized || !this.appInsights) {
        console.warn('Telemetry not initialized, event not tracked:', name);
        return;
      }

      this.appInsights.trackEvent({ name }, properties);
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  }

  trackMetric(name: string, value: number, properties?: Record<string, any>): void {
    try {
      if (!this.initialized || !this.appInsights) {
        return;
      }

      this.appInsights.trackMetric({
        name,
        average: value,
        properties,
      });
    } catch (error) {
      console.error('Failed to track metric:', error);
    }
  }

  trackTrace(message: string, properties?: Record<string, any>): void {
    try {
      if (!this.initialized || !this.appInsights) {
        return;
      }

      this.appInsights.trackTrace({
        message,
        properties,
      });
    } catch (error) {
      console.error('Failed to track trace:', error);
    }
  }

  getReactPlugin(): ReactPlugin | null {
    try {
      return this.reactPlugin;
    } catch (error) {
      console.error('Failed to get React plugin:', error);
      return null;
    }
  }

  addContext(key: string, value: string): void {
    try {
      if (!this.initialized || !this.appInsights) {
        return;
      }

      if (this.appInsights.context) {
        (this.appInsights.context as any)[`custom.${key}`] = value;
      }
    } catch (error) {
      console.error('Failed to add context:', error);
    }
  }

  setEnabled(isEnabled: boolean): void {
    try {
      if (!this.initialized || !this.appInsights) {
        return;
      }

      if (this.appInsights.config) {
        this.appInsights.config.disableTelemetry = !isEnabled;
      }
    } catch (error) {
      console.error('Failed to set telemetry enabled state:', error);
    }
  }
}

const telemetryService = new TelemetryService();
export default telemetryService;
